import React, { useState, useEffect } from 'react';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { AuthPrompt } from '@/components/ui/AuthPrompt';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, startOfDay } from 'date-fns';
import { ArrowLeft, Calendar, Clock, Share2, Settings } from 'lucide-react';
import { SlotsSlider } from '@/features/calendar/components/SlotsSlider';
import { ClassRequestModal } from '@/components/classRequests/ClassRequestModal';
import { IndividualRequestCard } from '@/components/classRequests/IndividualRequestCard';
import { DeleteConfirmationModal } from '@/components/ui/DeleteConfirmationModal';

import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { useClassRequestSubmission, useStudentRequests, useApproveClassRequest, useDenyClassRequest, useInstructorRequests } from '@/features/calendar/hooks/useClassRequests';
import { useInstructorSchedule, useInstructorScheduleCounts } from '@/features/calendar/hooks/useInstructorSchedule';
import { useUserAgendaConfig } from '@/features/calendar/hooks/useUserAgendaConfig';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { getCurrentDateString } from '@/utils/timezoneUtils';

interface Slot {
  slot_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  is_available: boolean;
}

interface DaySlot {
  date: Date;
  dayName: string;
  dayNumber: string;
  slotCount: number;
}

export const InstructorSlots: React.FC = () => {
  const { instructorUsername } = useParams<{ instructorUsername: string }>();
  const navigate = useNavigate();
  const { t, currentLanguage } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [instructorId, setInstructorId] = useState<string>('');
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptMessage, setAuthPromptMessage] = useState<string | undefined>();
  
  // Get date from URL params or default to today
  const dateParam = searchParams.get('date');
  const initialDate = dateParam ? (() => {
    // Parse date in local timezone to avoid UTC conversion issues
    const [year, month, day] = dateParam.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  })() : new Date();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [instructorName, setInstructorName] = useState('');
  const [instructorAvatar, setInstructorAvatar] = useState('');
  const [instructorBeltRank, setInstructorBeltRank] = useState('');
  const [instructorAcademy, setInstructorAcademy] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [slotToRemove, setSlotToRemove] = useState<Slot | null>(null);

  // Check if user is viewing their own profile (instructor) or someone else's
  const isOwnProfile = !!instructorId && instructorId === user?.id;

  // ── Single source of truth: RPC calculate_instructor_available_slots ──────────
  // Slots for the selected day (shown in the list below)
  const { slots: rpcSlots, isLoading: slotsLoading } = useInstructorSchedule(
    instructorId || undefined,
    selectedDate,
  );

  // Slot counts for the 14-day slider (parallel queries, shared cache)
  const scheduleCounts = useInstructorScheduleCounts(instructorId || undefined, 14);

  // Instructor requests (for showing pending/approved state on each slot)
  const { data: instructorRequests = [] } = useInstructorRequests(instructorId || '');

  // Agenda config — only for price/currency display, NOT for slot calculation
  const { config: agendaConfig } = useUserAgendaConfig(instructorId || '');

  // Convert RPC slots to the shape the rest of the page expects
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const slots = rpcSlots.map((s, i) => ({
    slot_id:          `slot-${i}`,
    start_time:       s.start_time.slice(0, 5),
    end_time:         s.end_time.slice(0, 5),
    duration_minutes: s.duration_minutes,
    is_available:     s.is_available,
  }));

  // Map 14-day counts to SlotsSlider format
  const dailySlotCounts = scheduleCounts.map(d => ({
    date:       d.date,
    slot_count: d.slotCount,
  }));

  const normalizeTime = (timeStr: string) => timeStr.split(':').slice(0, 2).join(':');

  // Hooks for class requests
  const queryClient = useQueryClient();
  const submitRequestMutation = useClassRequestSubmission();
  const { data: studentRequests = [] } = useStudentRequests(user?.id || '');
  const approveRequestMutation = useApproveClassRequest();
  const denyRequestMutation = useDenyClassRequest();


  useEffect(() => {
    if (instructorUsername) {
      loadInstructorData();
    }
  }, [instructorUsername]);

  const loadInstructorData = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name, profile_picture_url, belt_rank, jiu_jitsu_school')
        .eq('username', instructorUsername)
        .single();

      if (profile) {
        setInstructorId(profile.id);
        setInstructorName(profile.full_name || 'Instructor');
        setInstructorAvatar(profile.profile_picture_url || '');
        setInstructorBeltRank(profile.belt_rank || 'white');
        setInstructorAcademy(profile.jiu_jitsu_school || '');
      }
    } catch (error) {
      console.error('Error loading instructor data:', error);
    }
  };


  const handleDaySelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleSchedule = (slot: Slot) => {
    if (!user) {
      setAuthPromptMessage(t('auth.auth_prompt.schedule_class'));
      setShowAuthPrompt(true);
      return;
    }

    setSelectedSlot(slot);
    setIsRequestModalOpen(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedSlot || !instructorId || !user) return;

    try {
      await submitRequestMutation.mutateAsync({
        instructor_id: instructorId,
        student_id: user.id,
        requested_date: format(selectedDate, 'yyyy-MM-dd'),
        requested_start_time: selectedSlot.start_time,
        requested_end_time: selectedSlot.end_time,
        message: undefined
      });

      setIsRequestModalOpen(false);
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error submitting request:', error);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    if (!instructorId) return;
    
    try {
      await approveRequestMutation.mutateAsync({
        requestId,
        instructorId,
      });
      } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDenyRequest = async (requestId: string) => {
    if (!instructorId) return;
    
    try {
      await denyRequestMutation.mutateAsync({
        requestId,
        instructorId,
      });
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };

  const handleRemoveRequest = (slot: Slot) => {
    setSlotToRemove(slot);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemoveRequest = async () => {
    if (!slotToRemove || !instructorId || !user) {
      console.log('❌ Missing required data:', { slotToRemove, instructorId, user: !!user });
      return;
    }

    console.log('🔍 Looking for request to remove:', {
      instructorId,
      selectedDate: selectedDate.toISOString().split('T')[0],
      slotStartTime: slotToRemove.start_time,
      slotEndTime: slotToRemove.end_time,
      studentRequests: studentRequests.length
    });

    try {
      // Find the request to remove
      const requestToRemove = studentRequests.find(request => 
        request.instructor_id === instructorId &&
        request.requested_date === selectedDateStr &&
        normalizeTime(request.requested_start_time) === normalizeTime(slotToRemove.start_time) &&
        request.status === 'pending'
      );

      console.log('📋 Found request to remove:', requestToRemove);

      if (requestToRemove) {
        console.log('🗑️ Cancelling request:', requestToRemove.id);
        
        // Cancel the request by updating its status to 'cancelled'
        const { data: updateData, error } = await supabase
          .from('class_scheduling_requests')
          .update({ status: 'cancelled' })
          .eq('id', requestToRemove.id)
          .eq('student_id', user.id)  // RLS policy requires student_id filter
          .select('id, status');

        if (error) {
          console.error('❌ Error cancelling request:', error);
          console.error('❌ Error details:', JSON.stringify(error, null, 2));
          return;
        }

        console.log('📝 Update result:', updateData);
        console.log('📝 Number of rows updated:', updateData?.length || 0);

        if (updateData?.length === 0) {
          console.error('❌ No rows updated - RLS policy may be blocking the operation');
          toast({
            title: t('notifications.toasts.error_cancelling_request'),
            description: t('notifications.toasts.error_cancelling_request_description'),
            variant: "destructive"
          });
          return;
        }

        console.log('✅ Request cancelled successfully');

        // Show success toast
        toast({
          title: t('notifications.toasts.request_removed'),
          description: t('notifications.toasts.request_removed_description'),
          variant: "default"
        });

        // Refresh the data
        await queryClient.invalidateQueries({ queryKey: ['student-requests', user.id] });
        await queryClient.invalidateQueries({ queryKey: ['instructor-requests', instructorId] });
        await queryClient.refetchQueries({ queryKey: ['student-requests', user.id] });
      } else {
        console.log('❌ No matching request found');
        toast({
          title: t('notifications.toasts.request_not_found'),
          description: t('notifications.toasts.request_not_found_description'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Error removing request:', error);
      toast({
        title: t('notifications.toasts.error_removing_request'),
        description: t('notifications.toasts.error_removing_request_description'),
        variant: "destructive"
      });
    } finally {
      setShowRemoveConfirm(false);
      setSlotToRemove(null);
    }
  };

  const toggleSlotExpansion = (slotId: string) => {
    setExpandedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slotId)) {
        newSet.delete(slotId);
      } else {
        newSet.add(slotId);
      }
      return newSet;
    });
  };

  // Group requests by slot and date
  const getRequestsForSlot = (slot: Slot) => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const filteredRequests = instructorRequests.filter(request => 
      request.requested_date === selectedDateStr &&
      normalizeTime(request.requested_start_time) === normalizeTime(slot.start_time)
    );
    
    // Filter out past requests (only for instructor's own profile)
    if (isOwnProfile) {
      const now = new Date();
      const today = getCurrentDateString();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      return filteredRequests.filter(request => {
        // If request is for today, only show if time hasn't passed (with 2 hours buffer)
        if (request.requested_date === today) {
          const requestTime = parseInt(request.requested_start_time.split(':')[0]) * 60 + 
                             parseInt(request.requested_start_time.split(':')[1]);
          return requestTime > currentTime - 120; // 2 hours buffer
        }
        
        // If request is for future days (including tomorrow), keep it
        return request.requested_date >= today;
      });
    }
    
    return filteredRequests;
  };

  const isSlotRequested = (slot: Slot) => {
    if (!user) return false;
    
    const isRequested = studentRequests.some(request => 
      request.instructor_id === instructorId &&
      request.requested_date === selectedDate.toISOString().split('T')[0] &&
      normalizeTime(request.requested_start_time) === normalizeTime(slot.start_time) &&
      request.status === 'pending'
    );
    
    return isRequested;
  };

  const getSlotRequestStatus = (slot: Slot) => {
    if (!user) return null;
    
    const request = studentRequests.find(req => 
      req.instructor_id === instructorId &&
      req.requested_date === selectedDateStr &&
      normalizeTime(req.requested_start_time) === normalizeTime(slot.start_time)
    );
    
    // If request is cancelled, treat as if no request exists
    if (request?.status === 'cancelled') {
      return null;
    }
    
    return request?.status || null;
  };

  // Função para formatar horário baseado no idioma
  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    
    // Extrair apenas o horário (HH:MM:SS)
    const time = timeString.split('T')[1]?.split('.')[0] || timeString;
    const [hours, minutes] = time.split(':').map(Number);
    
    if (currentLanguage === 'en') {
      // Formato inglês: 12h com AM/PM
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
    } else {
      // Formato português: 24h
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  };


  const handleBack = () => {
    navigate(-1);
  };

  if (!instructorUsername) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white flex flex-col items-center justify-center px-4 text-center">
        <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
          <Calendar className="w-10 h-10 text-white/40" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
          {t('class_slots.instructor_not_found')}
        </h3>
        <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
          {t('class_slots.instructor_not_found_subtitle')}
        </p>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    const firstName = instructorName.split(' ')[0];
    const shareData = {
      title: `${firstName}'s Class Slots`,
      text: `Book a private class with ${firstName} on sOSSiety`,
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or share failed — silently ignore
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      toast({ description: t('class_slots.link_copied') });
    } catch {
      toast({ description: url, duration: 8000 });
    }
  };

  return (
    <>
      <div className="relative min-h-screen w-full bg-[#160C0C] pt-3 pb-6">
        <div className="px-4">
          {/* Header */}
          <div className="flex items-center gap-3 h-12 mb-2">
            <button
              onClick={handleBack}
              className="rounded-lg p-1.5 text-white/60 hover:text-white transition-colors shrink-0"
            >
              <ArrowLeft className="w-[22px] h-[22px]" />
            </button>

            <h1 className="flex-1 font-roboto font-semibold text-[17px] text-white">
              {t('class_slots.title')}
            </h1>

            <div className="flex items-center gap-0">
              {instructorUsername && (
                <button
                  onClick={handleShare}
                  className="h-9 w-9 flex items-center justify-center rounded-full text-[#8D8D8D] hover:text-white transition-colors"
                  aria-label="Share booking page"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              )}
              {isOwnProfile && (
                <button
                  onClick={() => navigate('/settings/instructor-tools')}
                  className="h-9 w-9 flex items-center justify-center rounded-full text-[#8D8D8D] hover:text-white transition-colors"
                  aria-label="Agenda settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Instructor card — matches Figma node 11359:3739 */}
          <div className="flex items-center gap-[14px] pt-4 pb-3 mb-2">
            {/* Avatar — clickable (→ profile) in public view, static in own view */}
            <FramedAvatar
              src={instructorAvatar || undefined}
              initials={(instructorName?.[0] ?? '?').toUpperCase()}
              beltRank={(instructorBeltRank as import('@/constants/belt').BeltColor) || 'white'}
              frameSize={{ w: 64, h: 70 }}
              size={44}
              isManual={isOwnProfile}
              username={!isOwnProfile ? instructorUsername : undefined}
            />

            {/* Name / username / academy */}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {!isOwnProfile ? (
                <button
                  onClick={() => navigate(`/profile/${instructorUsername}`)}
                  className="font-roboto font-bold text-[18px] text-white leading-tight truncate text-left hover:opacity-80 transition-opacity"
                >
                  {instructorName}
                </button>
              ) : (
                <p className="font-roboto font-bold text-[18px] text-white leading-tight truncate">
                  {instructorName}
                </p>
              )}
              {instructorUsername && (
                <p className="font-roboto text-[12px] text-white/50 leading-tight truncate">
                  @{instructorUsername}
                </p>
              )}
              {instructorAcademy && (
                <p className="font-roboto text-[12px] text-white/50 leading-tight truncate">
                  {instructorAcademy}
                </p>
              )}
            </div>

            {/* Price block — always rendered, 3 states */}
            {(() => {
              const hasPrice = !!(agendaConfig?.class_value && agendaConfig.class_value > 0);
              const currencySymbol = agendaConfig?.currency === 'BRL' ? 'R$ ' : '$ ';
              return (
                <div className="shrink-0 flex flex-col items-end min-w-[64px]">
                  <span className="font-roboto text-[11px] text-white/40 leading-tight">
                    {t('class_slots.solo_class')}
                  </span>
                  {hasPrice ? (
                    <>
                      <span className="font-roboto font-bold text-[18px] text-white leading-tight">
                        {currencySymbol}{agendaConfig!.class_value!.toFixed(0)}
                      </span>
                      <span className="font-roboto text-[11px] text-white/40 leading-tight">
                        {t('class_slots.per_session')}
                      </span>
                    </>
                  ) : isOwnProfile ? (
                    <button
                      onClick={() => navigate('/settings/instructor-tools')}
                      className="font-roboto font-medium text-[13px] text-sossiety-red leading-tight text-right hover:opacity-80 transition-opacity"
                    >
                      {t('class_slots.set_price')}
                    </button>
                  ) : (
                    <span className="font-roboto text-[12px] text-white/40 leading-tight text-right">
                      {t('class_slots.price_on_request')}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Slots Slider - Replacing the old Days Selector */}
          <SlotsSlider
            selectedDate={selectedDate}
            onDateSelect={handleDaySelect}
            dailySlotCounts={dailySlotCounts}
            hideMonthSelector={true}
          />

          {/* Date label — "Thursday, March 13 · N slots" */}
          <div className="pt-3 pb-2.5 px-0">
            <p className="font-roboto text-[13px] text-white/40">
              {format(selectedDate, 'EEEE, MMMM d')} · {slots.length} {slots.length === 1 ? t('class_slots.slot') : t('class_slots.slots')}
            </p>
          </div>

          {/* Slots List */}
          <div className="space-y-3">
            {slotsLoading ? (
              <div className="text-center py-8">
                <p className="text-white/60">{t('class_slots.loading_slots')}</p>
              </div>
            ) : (() => {
              // Check if selected date is in the past
              const today = new Date();
              const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              
              if (isPastDate) {
                return (
                  <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                    <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                      <Clock className="w-10 h-10 text-white/40" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                      Past Date
                    </h3>
                    <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                      No slots available for past dates. Please select a current or future date to view available time slots.
                    </p>
                  </div>
                );
              }
              
              // If not past date, check if there are slots
              if (slots.length > 0) {
                return 'hasSlots';
              } else {
                return 'noSlots';
              }
            })() === 'hasSlots' ? (
              slots.map((slot, index) => {
                const slotRequests = getRequestsForSlot(slot);
                const requestCount = slotRequests.length;
                const isOwnProfile = instructorId === user?.id;
                const slotId = slot.slot_id || `slot-${index}`;
                const isExpanded = expandedSlots.has(slotId);
                
                return (
                  <div key={slotId} className="space-y-3">
                    {/* Slot card */}
                    <div className="bg-[#2d2424] rounded-2xl px-[14px] py-[12px] min-h-[62px]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-[3.5px] min-w-0">
                          <p className="font-roboto font-medium text-[14px] text-white leading-[17.5px]">
                            {formatTime(slot.start_time)} {t('class_slots.to')} {formatTime(slot.end_time)}
                          </p>
                          <p className="font-roboto font-light text-[12px] text-white leading-[17.5px]">
                            {t('class_slots.private_class')} - {slot.duration_minutes} {t('class_slots.min')}
                          </p>
                        </div>
                        {isOwnProfile ? (
                          requestCount > 0 ? (
                            <button
                              onClick={() => toggleSlotExpansion(slotId)}
                              className="shrink-0 bg-sossiety-red text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-sossiety-red-hover transition-colors"
                            >
                              {requestCount} {requestCount === 1 ? t('class_request.request') : t('class_request.requests')}
                            </button>
                          ) : (
                            <span className="shrink-0 text-white/30 text-[12px] font-roboto">
                              {t('class_request.0_requests')}
                            </span>
                          )
                        ) : (
                          (() => {
                            const slotStatus = getSlotRequestStatus(slot);
                            if (slotStatus === 'pending') {
                              return (
                                <button
                                  onClick={() => handleRemoveRequest(slot)}
                                  className="shrink-0 bg-white/20 text-white/70 text-[10px] font-bold px-3 py-1 rounded-full hover:bg-white/30 transition-colors"
                                >
                                  {t('class_request.requested')}
                                </button>
                              );
                            } else if (slotStatus === 'approved') {
                              return (
                                <span className="shrink-0 bg-sossiety-success/20 text-sossiety-success text-[10px] font-bold px-3 py-1 rounded-full">
                                  {t('class_request.approved')}
                                </span>
                              );
                            } else if (slotStatus === 'denied') {
                              return (
                                <span className="shrink-0 bg-sossiety-red/20 text-sossiety-red text-[10px] font-bold px-3 py-1 rounded-full">
                                  {t('class_request.denied')}
                                </span>
                              );
                            } else {
                              return (
                                <button
                                  onClick={() => handleSchedule(slot)}
                                  className="shrink-0 border border-white/30 text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-white/10 hover:border-white/50 transition-colors"
                                >
                                  {t('class_slots.schedule')}
                                </button>
                              );
                            }
                          })()
                        )}
                      </div>
                    </div>

                    {/* Individual Request Cards - Only show when expanded and for instructor's own profile */}
                    {isOwnProfile && isExpanded && slotRequests.length > 0 && (
                      <div className="space-y-2 ml-4">
                        {slotRequests.map((request) => (
                          <IndividualRequestCard
                            key={request.request_id}
                            request={request}
                            onAccept={handleApproveRequest}
                            onDeny={handleDenyRequest}
                            isAccepting={approveRequestMutation.isPending}
                            isDenying={denyRequestMutation.isPending}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                {/* Empty State Icon */}
                <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                  <Calendar className="w-10 h-10 text-white/40" />
                </div>
                
                {/* Empty State Title */}
                <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                  {(() => {
                    const now = new Date();
                    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
                    return isToday ? t('class_slots.no_more_slots_today') : t('class_slots.no_slots_available');
                  })()}
                </h3>
                
                {/* Empty State Subtitle */}
                <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                  {(() => {
                    const now = new Date();
                    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
                    return isToday 
                      ? t('class_slots.no_more_slots_today_subtitle')
                      : t('class_slots.no_slots_subtitle');
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Class Request Modal */}
      {selectedSlot && (
        <ClassRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => {
            setIsRequestModalOpen(false);
            setSelectedSlot(null);
          }}
          slot={selectedSlot}
          instructorId={instructorId || ''}
          instructorName={instructorName}
          instructorAvatar={instructorAvatar}
          instructorBeltRank={instructorBeltRank}
          selectedDate={selectedDate}
          onSubmit={handleSubmitRequest}
          isLoading={submitRequestMutation.isPending}
        />
      )}

      {/* Remove Request Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false);
          setSlotToRemove(null);
        }}
        onConfirm={handleConfirmRemoveRequest}
        title={t('class_request.remove_class_request')}
        message={t('class_request.remove_request_confirm')}
        confirmText={t('class_request.remove_request')}
        isLoading={false}
      />

      {/* Auth Prompt for unauthenticated users */}
      <AuthPrompt
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        message={authPromptMessage}
      />
    </>
  );
};
